import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineDocument,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark
} from 'react-icons/hi2';
import { authService } from '../../../services';
import masterDataService from '../../../services/masterDataService';
import SharedModal from '../../../components/common/SharedModal';
import PageLoading from "../../../components/common/PageLoading";
import "../PlannedMaintenanceMasters.css";

const PmTriggerLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [formData, setFormData] = useState({
    pMScheduleId: '',
    triggeredAt: '',
    triggerType: '',
    triggeredBy: '',
    workOrderId: '',
    status: '',
    notes: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadData();
    } else {
      setError('Please login to view PM trigger logs');
      setLoading(false);
    }
  }, [tenantId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [logsData, schedulesData] = await Promise.all([
        masterDataService.getPMTriggerLogs(tenantId),
        masterDataService.getPMSchedules(tenantId)
      ]);
      setLogs(logsData || []);
      setSchedules(schedulesData || []);
    } catch (err) {
      setError('Failed to load data. Please try again.');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredLogs = logs.filter((log) =>
    log.triggerType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.status?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingLog(null);
    const now = new Date();
    const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setFormData({
      pMScheduleId: '',
      triggeredAt: localDateTime,
      triggerType: '',
      triggeredBy: '',
      workOrderId: '',
      status: '',
      notes: '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (log) => {
    setEditingLog(log);
    const formatDateTime = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    };
    setFormData({
      pMScheduleId: log.pMScheduleId?.toString() || '',
      triggeredAt: formatDateTime(log.triggeredAt),
      triggerType: log.triggerType || '',
      triggeredBy: log.triggeredBy || '',
      workOrderId: log.workOrderId?.toString() || '',
      status: log.status || '',
      notes: log.notes || '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      await masterDataService.deletePMTriggerLog(id);
      await loadData();
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete PM trigger log. Please try again.');
      console.error('Error deleting PM trigger log:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.pMScheduleId) {
      errors.pMScheduleId = 'Schedule is required';
    }
    if (!formData.triggeredAt) {
      errors.triggeredAt = 'Triggered at date/time is required';
    }
    if (!formData.triggerType.trim()) {
      errors.triggerType = 'Trigger type is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const logData = {
        tenantId: tenantId,
        pMScheduleId: parseInt(formData.pMScheduleId),
        triggeredAt: new Date(formData.triggeredAt),
        triggerType: formData.triggerType.trim(),
        triggeredBy: formData.triggeredBy.trim() || null,
        workOrderId: formData.workOrderId ? parseInt(formData.workOrderId) : null,
        status: formData.status.trim() || null,
        notes: formData.notes.trim() || null,
      };

      if (editingLog) {
        logData.triggerId = editingLog.triggerId;
        await masterDataService.updatePMTriggerLog(editingLog.triggerId, logData);
      } else {
        await masterDataService.createPMTriggerLog(logData);
      }

      await loadData();
      setShowModal(false);
      setFormData({ pMScheduleId: '', triggeredAt: '', triggerType: '', triggeredBy: '', workOrderId: '', status: '', notes: '' });
      setEditingLog(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to save PM trigger log. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingLog(null);
    setFormData({ pMScheduleId: '', triggeredAt: '', triggerType: '', triggeredBy: '', workOrderId: '', status: '', notes: '' });
    setFormErrors({});
  };

  const getScheduleName = (scheduleId) => {
    if (!scheduleId) return '-';
    return `Schedule ${scheduleId}`;
  };

  if (loading) {
    return (
      <div className="pm-masters-page">
        <PageLoading message="Loading PM trigger logs..." />
      </div>
    );
  }

  return (
    <div className="pm-masters-page">
      <div className="page-header">
        <div className="header-content">
          <h2>PM Trigger Logs</h2>
          <p>Manage PM trigger logs for Planned Maintenance</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus />
          Add Log
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search logs..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredLogs.length} {filteredLogs.length === 1 ? 'log' : 'logs'}
          </div>
        </div>

        {filteredLogs.length === 0 ? (
          <div className="empty-state">
            <HiOutlineDocument className="empty-icon" />
            <h3>No logs found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first PM trigger log'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                Add Log
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Schedule ID</th>
                <th>Triggered At</th>
                <th>Trigger Type</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((log) => (
                <tr key={log.triggerId}>
                  <td>{log.triggerId}</td>
                  <td>{log.pMScheduleId}</td>
                  <td>{log.triggeredAt ? new Date(log.triggeredAt).toLocaleString() : '-'}</td>
                  <td>{log.triggerType}</td>
                  <td>{log.status || '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(log)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(log)}
                        title="Delete"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingLog ? 'Edit PM Trigger Log' : 'Add New PM Trigger Log'}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="pm-trigger-log-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingLog
                  ? 'Updating...'
                  : 'Creating...'
                : editingLog
                ? 'Update'
                : 'Create'}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="pm-trigger-log-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="pMScheduleId">
                Schedule <span className="required">*</span>
              </label>
              <select
                id="pMScheduleId"
                value={formData.pMScheduleId}
                onChange={(e) =>
                  setFormData({ ...formData, pMScheduleId: e.target.value })
                }
                className={formErrors.pMScheduleId ? 'error' : ''}
              >
                <option value="">Select Schedule</option>
                {schedules.map((schedule) => (
                  <option key={schedule.pMScheduleId} value={schedule.pMScheduleId.toString()}>
                    Schedule {schedule.pMScheduleId}
                  </option>
                ))}
              </select>
              {formErrors.pMScheduleId && (
                <span className="field-error">{formErrors.pMScheduleId}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="triggeredAt">
                Triggered At <span className="required">*</span>
              </label>
              <input
                type="datetime-local"
                id="triggeredAt"
                value={formData.triggeredAt}
                onChange={(e) =>
                  setFormData({ ...formData, triggeredAt: e.target.value })
                }
                className={formErrors.triggeredAt ? 'error' : ''}
              />
              {formErrors.triggeredAt && (
                <span className="field-error">{formErrors.triggeredAt}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="triggerType">
                Trigger Type <span className="required">*</span>
              </label>
              <input
                type="text"
                id="triggerType"
                value={formData.triggerType}
                onChange={(e) =>
                  setFormData({ ...formData, triggerType: e.target.value })
                }
                className={formErrors.triggerType ? 'error' : ''}
                placeholder="Enter trigger type"
              />
              {formErrors.triggerType && (
                <span className="field-error">{formErrors.triggerType}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="triggeredBy">Triggered By</label>
              <input
                type="text"
                id="triggeredBy"
                value={formData.triggeredBy}
                onChange={(e) =>
                  setFormData({ ...formData, triggeredBy: e.target.value })
                }
                placeholder="Enter triggered by (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="workOrderId">Work Order ID</label>
              <input
                type="number"
                id="workOrderId"
                value={formData.workOrderId}
                onChange={(e) =>
                  setFormData({ ...formData, workOrderId: e.target.value })
                }
                placeholder="Enter work order ID (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <input
                type="text"
                id="status"
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
                placeholder="Enter status (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                placeholder="Enter notes (optional)"
                rows="3"
              />
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirm.triggerId)}
            >
              Delete
            </button>
          </>
        }
        customWidth="400px"
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete this trigger log?
          </p>
          <p className="warning-text" style={{ color: 'var(--status-error)', marginTop: '0.5rem' }}>
            This action cannot be undone.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default PmTriggerLogsPage;
